
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Truck, FileText } from 'lucide-react';
import { useData } from '../context/DataContext';
import LanguageSelector from './LanguageSelector';

const Navbar: React.FC = () => {
  const { settings, openQuoteModal } = useData();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [...(settings.navLinks || [])].sort((a, b) => a.order - b.order);

  const isHome = location.pathname === '/';

  const navClass = `fixed w-full z-50 transition-all duration-300 ${isHome && !scrolled ? 'bg-transparent py-6' : 'bg-automax-dark/95 backdrop-blur-md shadow-lg py-4 border-b border-gray-800'
    }`;

  return (
    <nav className={navClass}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">

          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 group">
            {settings.logoUrl ? (
              <img src={settings.logoUrl} alt={settings.siteName} className="w-auto object-contain" style={{ height: `${settings.logoHeight || '48'}px` }} />
            ) : (
              <>
                <div className="p-1.5 rounded transform skew-x-[-10deg] group-hover:scale-110 transition-transform" style={{ backgroundColor: settings.logoColor }}>
                  <Truck className="h-6 w-6 text-white skew-x-[10deg]" />
                </div>
                <span className="font-display text-3xl font-bold tracking-wider text-white">
                  {settings.siteName.slice(0, 4)}<span style={{ color: settings.logoColor }}>{settings.siteName.slice(4)}</span>
                </span>
              </>
            )}
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-6 lg:space-x-8">
            {navLinks.map((link) => (
              link.isExternal ? (
                <a
                  key={link.id}
                  href={link.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium uppercase tracking-wider text-gray-300 hover:text-white transition-colors"
                >
                  {link.name}
                </a>
              ) : (
                <Link
                  key={link.id}
                  to={link.path}
                  className={`text-sm font-medium uppercase tracking-wider hover:text-white transition-colors ${location.pathname === link.path ? 'text-white' : 'text-gray-300'
                    }`}
                  style={{ color: location.pathname === link.path ? settings.logoColor : undefined }}
                >
                  {link.name}
                </Link>
              )
            ))}
            <LanguageSelector />
            <button
              type="button"
              onClick={openQuoteModal}
              style={{ backgroundColor: settings.logoColor }}
              className="text-white px-5 py-2 rounded-sm font-display font-bold text-lg tracking-wide transition-all transform hover:-translate-y-0.5 flex items-center gap-2 shadow-lg shadow-black/20 cursor-pointer"
            >
              <FileText size={18} />
              Get Free Quote
            </button>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-4">
            <LanguageSelector />
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-300 hover:text-white focus:outline-none"
            >
              {isOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <div className={`md:hidden absolute top-full left-0 w-full bg-automax-dark border-t border-gray-800 shadow-xl transition-all duration-300 overflow-hidden ${isOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="px-4 pt-2 pb-6 space-y-2">
          {navLinks.map((link) => (
            link.isExternal ? (
              <a
                key={link.id}
                href={link.path}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setIsOpen(false)}
                className="block px-3 py-3 rounded-md text-base font-medium uppercase tracking-wide border-l-4 border-transparent text-gray-400 hover:text-white hover:bg-white/5"
              >
                {link.name}
              </a>
            ) : (
              <Link
                key={link.id}
                to={link.path}
                onClick={() => setIsOpen(false)}
                className={`block px-3 py-3 rounded-md text-base font-medium uppercase tracking-wide border-l-4 ${location.pathname === link.path ? 'bg-white/5 text-white' : 'border-transparent text-gray-400 hover:text-white hover:bg-white/5'
                  }`}
                style={{ borderColor: location.pathname === link.path ? settings.logoColor : 'transparent' }}
              >
                {link.name}
              </Link>
            )
          ))}
          <button
            onClick={() => { setIsOpen(false); openQuoteModal(); }}
            style={{ backgroundColor: settings.logoColor }}
            className="block w-full text-center text-white py-3 mt-4 rounded-sm font-display font-bold text-xl uppercase"
          >
            Get Free Quote
          </button>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
